import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import '../styles/AdminPanel.css';

type SchoolReferral = {
  id: string;
  email?: string;
  phoneNumber?: string;
  phoneDigits?: string;
  zipCode?: string;
  schoolName?: string;
  source?: string;
  createdAt?: Timestamp | Date | string | null;
};

function formatDate(value: SchoolReferral['createdAt']): string {
  if (!value) return '—';

  const date =
    value instanceof Timestamp
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ReferredSchoolsPage() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState<SchoolReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadReferrals() {
      setLoading(true);
      setError(null);

      try {
        const snap = await getDocs(query(collection(db, 'schoolReferrals'), orderBy('createdAt', 'desc')));
        if (cancelled) return;

        setReferrals(snap.docs.map(doc => ({ id: doc.id, ...(doc.data() as Omit<SchoolReferral, 'id'>) })));
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load referred schools.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadReferrals();

    return () => {
      cancelled = true;
    };
  }, []);

  const totalLabel = useMemo(() => {
    if (loading) return 'Loading referrals…';
    return `${referrals.length} referred ${referrals.length === 1 ? 'school' : 'schools'}`;
  }, [loading, referrals.length]);

  return (
    <div className="admin-panel">
      <div className="admin-content-card" style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>Referred Schools</h1>
            <p style={{ margin: '6px 0 0', color: '#6b7280' }}>{totalLabel}</p>
          </div>
          <button
            type="button"
            className="add-squad-btn"
            onClick={() => navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'schools' } })}
          >
            ← Back to Locations
          </button>
        </div>

        {error && (
          <div className="message error" style={{ marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '24px', color: '#6b7280' }}>Loading referred schools…</div>
        ) : referrals.length === 0 ? (
          <div style={{ padding: '24px', color: '#6b7280' }}>No referred schools yet.</div>
        ) : (
          <div className="table-wrapper">
            <table className="schools-table">
              <thead>
                <tr>
                  <th className="sticky-col">School</th>
                  <th>Email</th>
                  <th>Cell</th>
                  <th>ZIP</th>
                  <th>Source</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((referral, index) => (
                  <tr key={referral.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td className="sticky-col">
                      <span className="school-name">{referral.schoolName || '—'}</span>
                    </td>
                    <td>
                      {referral.email ? (
                        <a href={`mailto:${referral.email}`} style={{ color: '#4f46e5', fontWeight: 600 }}>
                          {referral.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{referral.phoneNumber || referral.phoneDigits || '—'}</td>
                    <td>{referral.zipCode || '—'}</td>
                    <td>{referral.source || '—'}</td>
                    <td>{formatDate(referral.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
